<template>
  <l-feature-group>
    <l-rectangle
      :fill-color="color"
      :fill-opacity="0.25"
      :color="color"
      :weight="2"
      :bounds="rectangleBounds"
      :interactive="false"
    />
    <l-circle-marker
      :lat-lng="endRef"
      :stroke="false"
      :fill="false"
      :interactive="false"
    >
      <l-tooltip
        :options="{
          permanent: true,
          direction: tooltipPosition,
          offset: tooltipOffset,
        }"
      >
        <div class="d-flex flex-column">
          <div>{{ rectangleSize }}</div>
          <div>{{ numOfAffectedStationsTooltip }}</div>
        </div>
      </l-tooltip>
    </l-circle-marker>
  </l-feature-group>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, watch } from 'vue';
import { LatLngBounds, LatLng } from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import {
  LRectangle,
  LTooltip,
  LFeatureGroup,
  LCircleMarker,
} from '@vue-leaflet/vue-leaflet';
import { ToolPurpose } from '@/stores/map/map-model';
import { Station } from '@/stores/station/station-model';
import { useStationSelector } from '@/composables/useStationSelector';
import StationStore from '@/stores/station/station-module';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StationRectangleSelector',
  components: {
    LRectangle,
    LTooltip,
    LFeatureGroup,
    LCircleMarker,
  },
  props: {
    startAnchor: { type: Object as PropType<LatLng>, required: true },
    endAnchor: { type: Object as PropType<LatLng>, required: true },
    map: { type: Object as PropType<LeafletMap>, required: true },
    finished: { type: Boolean, required: true },
    purpose: { type: Object as PropType<ToolPurpose>, required: true },
  },
  emits: ['remove'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const stationStore = StationStore;

    const rectangleSize = computed(() => {
      if (!startRef.value || !endRef.value || !props.map) return '';
      const x =
        props.map.distance(
          startRef.value,
          new LatLng(startRef.value.lat, endRef.value.lng)
        ) / 1000;
      const y =
        props.map.distance(
          startRef.value,
          new LatLng(endRef.value.lat, startRef.value.lng)
        ) / 1000;
      return `${Math.round(x)}km x ${Math.round(y)}km`;
    });

    const stationsInSelection = computed(() => {
      const stationsInRange: Station[] = [];
      for (const station of stationStore.allStations) {
        if (rectangleBounds.value?.contains(new LatLng(station.lat, station.lon))) {
          stationsInRange.push(station);
        }
      }
      return stationsInRange;
    });

    const {
      startAnchor: startRef,
      endAnchor: endRef,
      color,
      numOfAffectedStationsTooltip,
      tooltipPosition,
      tooltipOffset,
      handleFinished,
    } = useStationSelector(props, stationsInSelection, t);

    const rectangleBounds = computed(() => {
      if (!startRef.value || !endRef.value) return null;
      return new LatLngBounds(
        new LatLng(startRef.value.lat, startRef.value.lng),
        new LatLng(endRef.value.lat, endRef.value.lng)
      );
    });

    watch(() => props.finished, () => {
      if (props.finished) {
        emit('remove');
        handleFinished();
      }
    });

    return {
      startRef,
      endRef,
      color,
      numOfAffectedStationsTooltip,
      rectangleBounds,
      rectangleSize,
      tooltipPosition,
      tooltipOffset,
    };
  },
});
</script>
<style scoped></style>
